// Unified Buy feed (plan catalog-buy-unification, Track A / A1). The single
// Buy surface: one card per consolidated catalog listing, read from the
// F2-extended `/catalog/listings` API (never raw Supabase against the
// service-role-only catalog tables). Replaces the two old feeds — BuyScreen
// (direct-Supabase market preview) and CatalogScreen (imported-catalog list) —
// which A4 deletes once A3 reroutes past them.
//
// What it shows, all from the catalog API: a "starting at" price + 30-day move
// pill + sparkline + held count + colour swatches per card (CatalogListingCardView),
// and a "Market right now" insights rail computed client-side from the loaded
// page (computeCatalogInsights, mirroring the old Buy feed's computeInsights). Admin
// gating, keyset pagination, and pull-to-refresh are preserved from the screens
// it supersedes.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  Pressable,
  Image,
  TextInput,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { FlashList } from '@shopify/flash-list';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../styles/ThemeProvider';
import { isUserAdmin } from '../../utils/userPermissions';
import { getCatalogListings, type CatalogListingCard } from '../../lib/backendApi';
import CatalogListingCardView from '../../components/buy/CatalogListingCardView';
import { ChoiceChip } from '../../components/common/ChoiceChip';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { BuyStackParamList } from '../../types/navigation';

const PAGE_LIMIT = 24;

// Brands that currently have *priced* catalog inventory. The feed is ~8,900 cards
// across 700+ brands but only these have a tracked price today; the rest are
// unpriced imported listings that sort ahead of them by (random) listing id, so a
// purely page-derived chip row never surfaces these until you scroll very deep
// (e.g. the first Apple card sits ~page 36). Seed the chip row with them so they're
// always tappable. Curated by necessity — the catalog read path has no brand-list
// endpoint; replace this with a backend `GET /catalog/brands` (priced-first) when
// one ships. Brand *values* (not schema names), so the literal names are correct.
const PRIORITY_BRANDS = ['Apple', 'Microsoft', 'Sony'];

// --- Client-side "Market right now" insights -------------------------------
// Computed from the loaded cards, mirroring the old Buy feed's computeInsights but
// over the F2 catalog card shape: the feed card carries a scalar
// `price_delta_30d_pct` (not a series), so the "biggest mover" ranks on that
// delta directly rather than on a history array.

export type CatalogInsights = {
  biggestMover: CatalogListingCard | null;
  newArrivals: CatalogListingCard[];
  hottestHeld: CatalogListingCard | null;
};

export function computeCatalogInsights(cards: CatalogListingCard[]): CatalogInsights | null {
  // Biggest mover — top card by absolute 30-day delta. Needs an image and a
  // non-null delta so the rail card actually looks like a card.
  const eligibleMovers = cards.filter(
    (c) => c.hero_image_url != null && c.price_delta_30d_pct != null,
  );
  const biggestMover =
    [...eligibleMovers].sort(
      (a, b) => Math.abs(b.price_delta_30d_pct!) - Math.abs(a.price_delta_30d_pct!),
    )[0] ?? null;

  // New arrivals — the feed is newest-first, so the first few presentable cards
  // (image + a price) are the freshest entries worth surfacing.
  const newArrivals = cards
    .filter((c) => c.hero_image_url != null && c.starting_at_price_usd != null)
    .slice(0, 3);

  // Hot in inventory — top card by real held count. Image required for a visual
  // anchor; at least one held for sanity.
  const eligibleHeld = cards.filter((c) => c.hero_image_url != null && (c.held_count ?? 0) > 0);
  const hottestHeld =
    [...eligibleHeld].sort((a, b) => (b.held_count ?? 0) - (a.held_count ?? 0))[0] ?? null;

  // Nothing worth saying → null so the consumer omits the whole rail rather than
  // rendering an empty "Market right now" block.
  if (biggestMover == null && newArrivals.length === 0 && hottestHeld == null) {
    return null;
  }
  return { biggestMover, newArrivals, hottestHeld };
}

type Nav = NativeStackNavigationProp<BuyStackParamList>;

interface Props {
  navigation: Nav;
}

export default function UnifiedBuyFeed({ navigation }: Props) {
  const theme = useTheme();

  // Defence-in-depth: tab gating happens upstream, but the screen also refuses
  // to render real content for non-admins so deep-linking can't reach it.
  const [adminState, setAdminState] = useState<'pending' | 'allowed' | 'denied'>('pending');
  const [listings, setListings] = useState<CatalogListingCard[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  // Catalog-debugging filters (admin-only feed): narrow to a category slug, and/or to a
  // raw family name (set by tapping a card's family). Both map to the matching
  // `/catalog/listings` query params; null = no filter.
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  // Search: `searchInput` is the live TextInput value; `searchQuery` is its debounced
  // copy that actually drives the fetch (server-side `q` on /catalog/listings), so we
  // don't fire a request per keystroke across the ~8,900-card feed.
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Brands + categories discovered across loaded pages — append-only so the chip rows
  // stay stable when a filter narrows the result set. Derived client-side because the
  // catalog read path exposes no brand/category-list endpoint (we filter via the
  // existing `brand` / `category` query params).
  const [brandsSeen, setBrandsSeen] = useState<string[]>([]);
  const [categoriesSeen, setCategoriesSeen] = useState<string[]>([]);

  useEffect(() => {
    isUserAdmin().then((ok) => setAdminState(ok ? 'allowed' : 'denied'));
  }, []);

  // Debounce the search box: 300ms after the user stops typing, promote the trimmed
  // input to `searchQuery`, which the load effect below watches and re-fetches on.
  // Skip when the trimmed input already equals the active query (incl. the initial
  // empty mount) so we don't schedule a redundant no-op state update.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchQuery) return;
    const handle = setTimeout(() => setSearchQuery(trimmed), 300);
    return () => clearTimeout(handle);
  }, [searchInput, searchQuery]);

  const mergeDiscovered = useCallback((rows: CatalogListingCard[]) => {
    setBrandsSeen((prev) => {
      const next = new Set(prev);
      for (const r of rows) if (r.brand) next.add(r.brand);
      return [...next].sort((a, b) => a.localeCompare(b));
    });
    setCategoriesSeen((prev) => {
      const next = new Set(prev);
      for (const r of rows) if (r.category) next.add(r.category);
      return [...next].sort((a, b) => a.localeCompare(b));
    });
  }, []);

  // Monotonic request id — the same out-of-order guard the old feeds used: only
  // the latest first-page fetch is allowed to write state.
  const requestIdRef = useRef(0);

  const loadFirstPage = useCallback(
    async (brand: string | null, category: string | null, family: string | null, q: string) => {
      const myId = ++requestIdRef.current;
      try {
        const page = await getCatalogListings({
          limit: PAGE_LIMIT,
          ...(brand ? { brand } : {}),
          ...(category ? { category } : {}),
          ...(family ? { family } : {}),
          ...(q ? { q } : {}),
        });
        if (myId !== requestIdRef.current) return;
        setListings(page.listings);
        setCursor(page.next_cursor);
        mergeDiscovered(page.listings);
      } catch (err) {
        if (myId === requestIdRef.current) {
          console.error('[UnifiedBuyFeed] loadFirstPage failed:', err);
        }
      } finally {
        if (myId === requestIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [mergeDiscovered],
  );

  useEffect(() => {
    if (adminState !== 'allowed') return;
    setLoading(true);
    loadFirstPage(selectedBrand, selectedCategory, selectedFamily, searchQuery);
  }, [adminState, selectedBrand, selectedCategory, selectedFamily, searchQuery, loadFirstPage]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadFirstPage(selectedBrand, selectedCategory, selectedFamily, searchQuery);
  }, [loadFirstPage, selectedBrand, selectedCategory, selectedFamily, searchQuery]);

  const onEndReached = useCallback(async () => {
    // Keyset-paginate: stop when there's no cursor, or a page is already loading.
    if (!cursor || loadingMore || loading) return;
    // Same generation guard as loadFirstPage: capture the current request id (a brand
    // change / pull-to-refresh bumps it via loadFirstPage). If a newer first-page load
    // supersedes this append while it's in flight, drop its result rather than mixing a
    // stale brand/page into the now-current list. We don't bump the id here — this is a
    // continuation of the current generation, not a new request.
    const myId = requestIdRef.current;
    setLoadingMore(true);
    try {
      const page = await getCatalogListings({
        limit: PAGE_LIMIT,
        cursor,
        ...(selectedBrand ? { brand: selectedBrand } : {}),
        ...(selectedCategory ? { category: selectedCategory } : {}),
        ...(selectedFamily ? { family: selectedFamily } : {}),
        ...(searchQuery ? { q: searchQuery } : {}),
      });
      if (myId !== requestIdRef.current) return;
      setListings((prev) => [...prev, ...page.listings]);
      setCursor(page.next_cursor);
      mergeDiscovered(page.listings);
    } catch (err) {
      if (myId === requestIdRef.current) {
        console.error('[UnifiedBuyFeed] onEndReached failed:', err);
      }
    } finally {
      setLoadingMore(false);
    }
  }, [
    cursor,
    loadingMore,
    loading,
    selectedBrand,
    selectedCategory,
    selectedFamily,
    searchQuery,
    mergeDiscovered,
  ]);

  // Rail only on the unfiltered view (any active filter/search is itself a "narrow", so a
  // "market right now" overview would be redundant / confusing there).
  const anyFilterActive =
    selectedBrand != null ||
    selectedCategory != null ||
    selectedFamily != null ||
    searchQuery !== '';
  const insights = useMemo(
    () => (anyFilterActive ? null : computeCatalogInsights(listings)),
    [anyFilterActive, listings],
  );

  // Chip row: the priced/priority brands first (always present), then the brands
  // discovered while paginating, deduped case-insensitively so a priority brand
  // that also loads from a page isn't shown twice.
  const brandChips = useMemo(() => {
    const seen = new Set(PRIORITY_BRANDS.map((b) => b.toLowerCase()));
    const rest = brandsSeen.filter((b) => !seen.has(b.toLowerCase()));
    return [...PRIORITY_BRANDS, ...rest];
  }, [brandsSeen],
  );

  // Tapping a card's family narrows the feed to that raw family name (the core
  // wrong-family debugging move). A category is implied but not forced — clearing the
  // family chip returns to the prior category/brand view.
  const onFilterFamily = useCallback((family: string) => {
    setSelectedFamily(family);
  }, []);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { flex: 1, backgroundColor: theme.colors.backgroundPrimary },
        centred: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        header: {
          paddingHorizontal: theme.spacing.lg,
          paddingTop: theme.spacing.lg,
          paddingBottom: theme.spacing.md,
        },
        previewBadge: {
          alignSelf: 'flex-start',
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          paddingHorizontal: theme.spacing.sm,
          paddingVertical: theme.spacing.xxs,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          borderRadius: theme.radius.radiusSm,
          marginBottom: theme.spacing.sm,
        },
        previewBadgeText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        },
        title: { ...theme.typography.h3, color: theme.colors.textPrimary },
        subtitle: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xxs,
        },
        // Catalog search box — mirrors the search style on CacheScreen.
        searchBox: {
          ...theme.typography.md,
          color: theme.colors.textPrimary,
          backgroundColor: theme.colors.backgroundElevated,
          borderRadius: theme.radius.radiusMd,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          paddingHorizontal: theme.spacing.md,
          paddingVertical: theme.spacing.sm,
          marginTop: theme.spacing.md,
        },
        // Brand / category filter chip rows (chips are ChoiceChip).
        brandChipRow: {
          flexDirection: 'row',
          gap: theme.spacing.sm,
          paddingHorizontal: theme.spacing.lg,
          paddingBottom: theme.spacing.sm,
        },
        // Small uppercase label above each filter chip row so the two rows
        // (brand / category) are distinguishable.
        chipRowLabel: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          paddingHorizontal: theme.spacing.lg,
          paddingTop: theme.spacing.sm,
        },
        // Active-family banner — the wrong-family debugging context. Accent-tinted so
        // it reads as "you are filtered", with a tap-to-clear affordance.
        familyBanner: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          alignSelf: 'flex-start',
          marginHorizontal: theme.spacing.lg,
          marginTop: theme.spacing.sm,
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: theme.colors.accent,
          borderRadius: theme.radius.radiusLg,
        },
        familyBannerText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.onAccent,
        },
        // Insights rail.
        insightsHeading: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 1,
          paddingHorizontal: theme.spacing.lg,
          paddingTop: theme.spacing.md,
        },
        insightsRail: {
          flexDirection: 'row',
          gap: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          paddingTop: theme.spacing.md,
          paddingBottom: theme.spacing.sm,
        },
        insightCard: {
          width: 220,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.md,
        },
        insightLabelRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginBottom: theme.spacing.sm,
        },
        insightLabel: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
        },
        insightContent: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
        insightThumb: {
          width: 48,
          height: 48,
          borderRadius: theme.radius.radiusSm,
          backgroundColor: theme.colors.backgroundElevated,
        },
        insightThumbRow: { flexDirection: 'row', gap: 4 },
        insightThumbSmall: {
          width: 36,
          height: 36,
          borderRadius: theme.radius.radiusSm,
          backgroundColor: theme.colors.backgroundElevated,
        },
        insightBody: { flex: 1, minWidth: 0 },
        insightProductName: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        insightStat: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.mono,
          color: theme.colors.textSecondary,
          marginTop: 2,
        },
        listContent: { paddingBottom: 100 },
        footer: { paddingVertical: theme.spacing.lg, alignItems: 'center' },
        message: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginTop: theme.spacing.md,
        },
      }),
    [theme],
  );

  const goToListing = useCallback(
    (card: CatalogListingCard) =>
      navigation.navigate('CatalogListing', {
        listingId: card.listing_id,
        listingTitle: card.listing_title,
      }),
    [navigation],
  );

  if (adminState === 'pending') {
    return (
      <SafeAreaView style={[styles.container, styles.centred]} edges={['top']}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </SafeAreaView>
    );
  }

  if (adminState === 'denied') {
    return (
      <SafeAreaView style={[styles.container, styles.centred]} edges={['top']}>
        <Ionicons name="lock-closed-outline" size={32} color={theme.colors.textTertiary} />
        <Text style={styles.message}>Buy is in admin-only preview right now.</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Fixed (non-scrolling) header so the search box keeps focus across the list's
          re-renders as results stream in. */}
      <View style={styles.header}>
        <View style={styles.previewBadge}>
          <Ionicons name="construct-outline" size={12} color={theme.colors.accent} />
          <Text style={styles.previewBadgeText}>Preview</Text>
        </View>
        <Text style={styles.title}>Buy</Text>
        <Text style={styles.subtitle}>Browse the catalog</Text>
        <TextInput
          style={styles.searchBox}
          placeholder="Search the catalog"
          placeholderTextColor={theme.colors.textTertiary}
          value={searchInput}
          onChangeText={setSearchInput}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="search"
          clearButtonMode="while-editing"
        />
      </View>
      <FlashList
        data={listings}
        keyExtractor={(l) => l.listing_id}
        estimatedItemSize={96}
        contentContainerStyle={styles.listContent as object}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.6}
        keyboardShouldPersistTaps="handled"
        ListHeaderComponent={
          <View>
            {insights && (
              <>
                <Text style={styles.insightsHeading}>Market right now</Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.insightsRail}
                >
                  {insights.biggestMover && (
                    <Pressable
                      style={styles.insightCard}
                      onPress={() => goToListing(insights.biggestMover!)}
                    >
                      <View style={styles.insightLabelRow}>
                        <Ionicons
                          name="trending-up"
                          size={12}
                          color={
                            (insights.biggestMover.price_delta_30d_pct ?? 0) >= 0
                              ? theme.colors.trendUp
                              : theme.colors.trendDown
                          }
                        />
                        <Text style={styles.insightLabel}>Biggest mover</Text>
                      </View>
                      <View style={styles.insightContent}>
                        {insights.biggestMover.hero_image_url && (
                          <Image
                            source={{ uri: insights.biggestMover.hero_image_url }}
                            style={styles.insightThumb}
                            resizeMode="cover"
                          />
                        )}
                        <View style={styles.insightBody}>
                          <Text style={styles.insightProductName} numberOfLines={2}>
                            {insights.biggestMover.listing_title}
                          </Text>
                          <Text
                            style={[
                              styles.insightStat,
                              {
                                color:
                                  (insights.biggestMover.price_delta_30d_pct ?? 0) >= 0
                                    ? theme.colors.trendUp
                                    : theme.colors.trendDown,
                              },
                            ]}
                          >
                            {(insights.biggestMover.price_delta_30d_pct ?? 0) >= 0 ? '+' : ''}
                            {insights.biggestMover.price_delta_30d_pct?.toFixed(1)}% · 30d
                          </Text>
                        </View>
                      </View>
                    </Pressable>
                  )}

                  {insights.newArrivals.length > 0 && (
                    <Pressable
                      style={styles.insightCard}
                      onPress={() => goToListing(insights.newArrivals[0])}
                    >
                      <View style={styles.insightLabelRow}>
                        <Ionicons name="sparkles" size={12} color={theme.colors.accent} />
                        <Text style={styles.insightLabel}>New to Cache</Text>
                      </View>
                      <View style={styles.insightContent}>
                        <View style={styles.insightThumbRow}>
                          {insights.newArrivals.slice(0, 3).map((c) =>
                            c.hero_image_url ? (
                              <Image
                                key={c.listing_id}
                                source={{ uri: c.hero_image_url }}
                                style={styles.insightThumbSmall}
                                resizeMode="cover"
                              />
                            ) : (
                              <View key={c.listing_id} style={styles.insightThumbSmall} />
                            ),
                          )}
                        </View>
                      </View>
                      <Text
                        style={[styles.insightStat, { marginTop: theme.spacing.xs }]}
                        numberOfLines={1}
                      >
                        {insights.newArrivals.length} just added
                      </Text>
                    </Pressable>
                  )}

                  {insights.hottestHeld && (
                    <Pressable
                      style={styles.insightCard}
                      onPress={() => goToListing(insights.hottestHeld!)}
                    >
                      <View style={styles.insightLabelRow}>
                        <Ionicons name="flame" size={12} color={theme.colors.accent} />
                        <Text style={styles.insightLabel}>Hot in inventory</Text>
                      </View>
                      <View style={styles.insightContent}>
                        {insights.hottestHeld.hero_image_url && (
                          <Image
                            source={{ uri: insights.hottestHeld.hero_image_url }}
                            style={styles.insightThumb}
                            resizeMode="cover"
                          />
                        )}
                        <View style={styles.insightBody}>
                          <Text style={styles.insightProductName} numberOfLines={2}>
                            {insights.hottestHeld.listing_title}
                          </Text>
                          <Text style={styles.insightStat}>
                            {insights.hottestHeld.held_count} held in Cache
                          </Text>
                        </View>
                      </View>
                    </Pressable>
                  )}
                </ScrollView>
              </>
            )}
            {brandChips.length > 0 && (
              <>
                <Text style={styles.chipRowLabel}>Brand</Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.brandChipRow}
                >
                  <ChoiceChip
                    label="All brands"
                    selected={selectedBrand === null}
                    onPress={() => setSelectedBrand(null)}
                  />
                  {brandChips.map((b) => (
                    <ChoiceChip
                      key={b}
                      label={b}
                      selected={selectedBrand === b}
                      onPress={() => setSelectedBrand(b)}
                    />
                  ))}
                </ScrollView>
              </>
            )}
            {categoriesSeen.length > 0 && (
              <>
                <Text style={styles.chipRowLabel}>Category</Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.brandChipRow}
                >
                  <ChoiceChip
                    label="All categories"
                    selected={selectedCategory === null}
                    onPress={() => setSelectedCategory(null)}
                  />
                  {categoriesSeen.map((c) => (
                    <ChoiceChip
                      key={c}
                      label={c}
                      selected={selectedCategory === c}
                      onPress={() => setSelectedCategory(c)}
                    />
                  ))}
                </ScrollView>
              </>
            )}
            {selectedFamily != null && (
              <Pressable
                style={styles.familyBanner}
                onPress={() => setSelectedFamily(null)}
                accessibilityRole="button"
              >
                <Ionicons name="funnel" size={12} color={theme.colors.onAccent} />
                <Text style={styles.familyBannerText}>family: {selectedFamily}</Text>
                <Ionicons name="close" size={14} color={theme.colors.onAccent} />
              </Pressable>
            )}
          </View>
        }
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.colors.accent}
          />
        }
        renderItem={({ item }) => (
          <CatalogListingCardView
            listing={item}
            onPress={() => goToListing(item)}
            onFilterFamily={onFilterFamily}
          />
        )}
        ListFooterComponent={
          loadingMore ? (
            <View style={styles.footer}>
              <ActivityIndicator color={theme.colors.accent} />
            </View>
          ) : null
        }
        ListEmptyComponent={
          loading ? (
            <View style={styles.centred}>
              <ActivityIndicator size="large" color={theme.colors.accent} />
            </View>
          ) : (
            <View style={styles.centred}>
              <Text style={styles.message}>
                {anyFilterActive
                  ? 'No listings match these filters.'
                  : 'No listings in the catalog yet.'}
              </Text>
            </View>
          )
        }
      />
    </SafeAreaView>
  );
}
